import type { Channel } from "@sobidogam/platform-ports";
import { useEffect, useState } from "react";
import { navigate } from "./chrome";
import { HELP_ITEMS, searchHelp } from "./help-content";
import { IssueReportButton } from "./IssueReport";

/**
 * #48 도움말에서 답 찾기. Used on /support and /app/help. A "?" link from a
 * screen opens /app/help#<id> with that answer expanded.
 */
export function HelpSearch({
	channel,
	initialId,
	onSearch,
	onIssueOpen,
}: {
	channel: Channel;
	initialId?: string | undefined;
	onSearch?: (resultCount: number) => void;
	onIssueOpen?: () => void;
}) {
	const [query, setQuery] = useState("");
	const results = searchHelp(query);
	useEffect(() => {
		if (!initialId) return;
		document
			.getElementById(`help-${initialId}`)
			?.scrollIntoView({ block: "start" });
	}, [initialId]);
	useEffect(() => {
		if (!query.trim()) return;
		const timer = window.setTimeout(() => onSearch?.(results.length), 800);
		return () => window.clearTimeout(timer);
	}, [query, results.length, onSearch]);
	return (
		<section className="help-search" aria-label="도움말 찾기">
			<label className="help-input">
				<span className="sr-only">도움말 검색</span>
				<input
					type="search"
					value={query}
					placeholder="예: 월 시작, 백업, 아이폰"
					onChange={(event) => setQuery(event.target.value)}
				/>
			</label>
			<p className="field-hint">
				{query.trim()
					? `${results.length}개 찾았어요`
					: `자주 묻는 질문 ${HELP_ITEMS.length}개`}
			</p>
			{results.length ? (
				<div className="help-list">
					{results.map((item) => (
						<details
							key={item.id}
							id={`help-${item.id}`}
							open={item.id === initialId || undefined}
						>
							<summary>{item.q}</summary>
							<p>{item.a}</p>
							{item.action ? (
								<a
									className="text-link"
									href={item.action.href}
									onClick={(event) => {
										if (!item.action) return;
										event.preventDefault();
										navigate(item.action.href);
									}}
								>
									{item.action.label} →
								</a>
							) : null}
						</details>
					))}
				</div>
			) : (
				<div className="household-empty compact">
					<strong>찾는 답이 없어요.</strong>
					<p>어떤 일이 있었는지 알려 주시면 답장드릴게요.</p>
				</div>
			)}
			<IssueReportButton
				channel={channel}
				screen="도움말"
				label="찾는 답이 없으면 알려 주세요"
				{...(onIssueOpen ? { onOpen: onIssueOpen } : {})}
			/>
		</section>
	);
}
